import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { FaWhatsapp, FaArrowRightFromBracket, FaRegFaceSmile } from 'react-icons/fa6';

import { clearDataAction } from '../../../redux/channelsDuck';
import { getConversationsAction } from '../../../redux/conversationsDuck';
import { getJcnError } from '../../../const/labelService';
import { logout as logoutRequest } from '../../../utils/userAuth';
import {
  ModalContent,
  DropdownBody,
  SpaceCard,
  SpaceImg,
  SpaceContent,
  SpaceName,
  SpaceMeta,
  WaIcon,
  SpacePhones,
  DropdownFooter,
  CloseButton,
} from './SidebarModal.style';
import { setActualSpaceAction } from '../../../redux/spacesDuck';
import FadeImage from '../../shared/components/FadeImage/FadeImage';
import toastConfig from '../../shared/constants/toaster-config';
import { endPartnerAccess, hasPartnerAccessState } from '../../../utils/partnerAccess/partnerDirectory';
import { isPartnerAccessEnabled } from '../../../utils/partnerAccess/partnerFlag';
import { goToPartnerOverview } from '../../../views/partnerOverview/navigation';
import { SEDE_LOST_REASONS, storeSedeLostNotice } from '../../../utils/partnerAccess/sedeSession';
import { formatWhatsappPhones } from './whatsappPhones';
import SedeSwitcher from './SedeSwitcher.component';
import { LogoutIcon } from './sedeIcons';

// Opening the dropdown puts the cursor in the search of the sedes. Not on a
// touch screen: there the focus brings up a keyboard that covers the sedes.
const hasTouchPointer = () => Boolean(window.matchMedia?.('(pointer: coarse)')?.matches);

// A logout that fails once partner access has ended cannot show a toast: the
// tab is about to reload. The notice survives the reload; Sentry gets the
// failure (no token, no id).
const reportLogoutFailedAfterPartnerAccess = (error) => {
  storeSedeLostNotice(SEDE_LOST_REASONS.LOGOUT_FAILED);
  try {
    Sentry.captureException(error, {
      tags: { component: 'sidebarModal', op: 'logoutAfterPartnerAccess' },
    });
  } catch (e) {
    // Telemetry must never keep the tab rendering a sede it already left.
  }
};

const SidebarModalComponent = ({ show, searchFocusRequest, onClose, anchorRef }) => {
  const ref = useRef(null);
  const [isActive, setIsActive] = useState(show);
  // What the sede switcher found: the sedes of a partner are on screen.
  const [hasSedes, setHasSedes] = useState(false);
  const token = useSelector((state) => state.user.data.token);
  const userId = useSelector((state) => state.user.data.id);
  const spaces = useSelector((state) => state.spaces.data);
  const selectedSpace = useSelector((state) => state.spaces.selected);
  const history = useHistory();
  const dispatch = useDispatch();

  // A request for the search also opens a dropdown that a click outside closed.
  useEffect(() => {
    setIsActive(show);
  }, [show, searchFocusRequest]);

  // Every way the dropdown closes itself goes through here: whoever opened it
  // is told, so what it knows about the dropdown is always true.
  const close = () => {
    setIsActive(false);
    if (onClose) onClose();
  };

  // A click anywhere else closes the dropdown. Not one on its anchor: the
  // buttons there open and close it themselves, and a dropdown that closed on
  // their click first would have them open it again.
  useEffect(() => {
    if (!isActive) return undefined;
    const closeOnClickOutside = (event) => {
      const holdsTheClick = (element) => Boolean(element) && element.contains(event.target);
      if (holdsTheClick(ref.current) || holdsTheClick(anchorRef?.current)) return;
      close();
    };
    document.addEventListener('click', closeOnClickOutside, true);
    return () => document.removeEventListener('click', closeOnClickOutside, true);
  }, [isActive, onClose, anchorRef]);

  // A tab that already holds partner access is known before the sedes load.
  const partnerPanel = isPartnerAccessEnabled() && (hasSedes || hasPartnerAccessState());
  const focusOnOpen = searchFocusRequest > 0 || !hasTouchPointer();
  const searchFocus = isActive && focusOnOpen ? searchFocusRequest + 1 : 0;

  const openOverview = () => {
    close();
    goToPartnerOverview(history);
  };

  const logout = async () => {
    let partnerAccessEnded = false;
    try {
      const session = 'front-app';
      if (hasPartnerAccessState()) {
        // Partner access: the partner session is revoked and the tab leaves
        // the sede BEFORE the logout, so the request below goes out with the
        // normal JWT. With a delegated token the backend ignores a logout.
        await endPartnerAccess();
        partnerAccessEnded = true;
        await logoutRequest(localStorage.getItem('token'), session);
      } else {
        await logoutRequest(token, session);
      }
      history.replace('/login');
      dispatch(clearDataAction());
    } catch (error) {
      if (partnerAccessEnded) {
        // The tab still renders the sede, but it already left it and its
        // requests use the normal JWT. Full reload on purpose: the host
        // rebuilds all its state from /verifyToken, as the normal session.
        reportLogoutFailedAfterPartnerAccess(error);
        window.location.assign('/chat');
        return;
      }
      toast(getJcnError(error.message), toastConfig);
    }
  };

  const changeSpace = async (space) => {
    try {
      await dispatch(setActualSpaceAction(space));
      await dispatch(getConversationsAction(true, 10));
      close();
    } catch (error) {
      console.error(error);
      toast.error(getJcnError(error.message), toastConfig);
    }
  };

  const getChannels = (space) => formatWhatsappPhones(space.channels);

  return (
    <ModalContent ref={ref} open={isActive} partner={partnerPanel} data-partner-panel={partnerPanel ? '' : undefined}>
      <DropdownBody data-dropdown-body partner={partnerPanel}>
        {spaces?.map((space) => (
          <SpaceCard
            key={space.id}
            selected={selectedSpace?.id === space?.id}
            onClick={() => changeSpace(space)}
          >
            <SpaceImg>
              {space.imageUrl ? (
                <FadeImage image={space.imageUrl} />
              ) : (
                <FaRegFaceSmile style={{ fontSize: '1.2rem', color: '#111827' }} />
              )}
            </SpaceImg>
            <SpaceContent>
              <SpaceName>{space.name}</SpaceName>
              {getChannels(space) && (
                <SpaceMeta>
                  <WaIcon>
                    <FaWhatsapp />
                  </WaIcon>
                  <SpacePhones>{getChannels(space)}</SpacePhones>
                </SpaceMeta>
              )}
            </SpaceContent>
          </SpaceCard>
        ))}
        <SedeSwitcher
          open={isActive}
          userId={userId}
          firstBlock={!spaces?.length}
          onOverview={openOverview}
          onPartnerChange={setHasSedes}
          searchFocus={searchFocus}
        />
      </DropdownBody>
      <DropdownFooter partner={partnerPanel}>
        <CloseButton onClick={logout} partner={partnerPanel}>
          {partnerPanel ? <LogoutIcon /> : <FaArrowRightFromBracket />}
          Cerrar sesión
        </CloseButton>
      </DropdownFooter>
    </ModalContent>
  );
};

SidebarModalComponent.propTypes = {
  show: PropTypes.bool.isRequired,
  // Each new value opens the dropdown, if it is closed, on the sede search.
  searchFocusRequest: PropTypes.number,
  // Told each time the dropdown closes itself: a click outside it, a space
  // chosen, the overview opened. Without it the dropdown still closes.
  onClose: PropTypes.func,
  // What holds the buttons that open and close the dropdown. Give it along
  // with `onClose`: a click there is theirs, not a click outside.
  anchorRef: PropTypes.shape({ current: PropTypes.object }),
};

SidebarModalComponent.defaultProps = {
  searchFocusRequest: 0,
  onClose: undefined,
  anchorRef: undefined,
};

export default SidebarModalComponent;
